import {
  Button,
  Dialog,
  DialogContent,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  LoadingSpinner,
  useToast,
} from '@ais-ui/ui-core';
import React, { useEffect, useMemo, useState } from 'react';

import {
  ReportTemplate,
  useGetShareGroups,
  useShareReport,
  useGetReport,
} from '@ais-ui/websockets';
import { useForm, UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useLocalization } from '@ais-ui/locale-management';
import { GroupMultiSelect } from '../../../Groups/GroupMultiSelect';
import { SelectType } from '@ais-ui/ui-core';
import { createShapeDictionary } from '../utils/createShapeDictionary';
import { useFlattenedGeometryShapes } from '@ais-ui/api';
import { reportTypesObject } from '../../reportsTypes';
import { useOwnershipCheck } from '../../../utils/useOwnershipCheck';

interface ShareReportProps {
  reportTemplate: ReportTemplate;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSharingChange?: (isSharing: boolean) => void;
}

const shareReportFormSchema = z.object({
  share_with: z
    .array(
      z.object({
        label: z.string(),
        value: z.string(),
      }),
    )
    .min(0, 'Please select at least one group or leave empty to unshare'),
});

type ShareReportValues = z.infer<typeof shareReportFormSchema>;

export const ShareReport = ({
  reportTemplate,
  open,
  onOpenChange,
  onSharingChange,
}: ShareReportProps) => {
  const { messages } = useLocalization();
  const { mutate } = useShareReport();
  const { toast } = useToast();
  const { data: shareGroups, isLoading: isLoadingGroups } = useGetShareGroups();
  const { data: shapes } = useFlattenedGeometryShapes();
  const { isOwner } = useOwnershipCheck();

  // Get the most recent job from the report template to fetch report data
  const mostRecentJob = reportTemplate.jobs?.find((job) => job.uuid) || null;
  const { data: reportData } = useGetReport(mostRecentJob?.uuid || '', 'UTC');

  const [isSubmitting, setIsSubmitting] = useState(false);

  const reportType = useMemo(() => {
    if (!reportTemplate) return null;
    return reportTypesObject[reportTemplate.type];
  }, [reportTemplate]);

  // Convert reportTemplate.groups (string[]) to SelectType[] for the form
  const initialShareWith = useMemo(() => {
    if (!shareGroups || !reportTemplate.groups) return [];
    return reportTemplate.groups
      .map((groupUuid) => {
        const group = shareGroups.find((g) => g.uuid === groupUuid);
        return group ? { label: group.name, value: group.uuid } : null;
      })
      .filter((item): item is SelectType => item !== null);
  }, [shareGroups, reportTemplate.groups]);

  const form: UseFormReturn<ShareReportValues> = useForm<ShareReportValues>({
    resolver: zodResolver(shareReportFormSchema),
    defaultValues: {
      share_with: [],
    },
  });

  useEffect(() => {
    form.reset({
      share_with: initialShareWith,
    });
  }, [form, initialShareWith]);

  const onSubmit = async (data: ShareReportValues) => {
    if (!reportTemplate) {
      toast({
        className: 'share-report-error-toast',
        title: messages.genericToastTitleError,
        description: 'Missing required data for sharing. Please try again.',
        variant: 'destructive',
      });
      return;
    }

    // Check if we have report data with parsed parameters
    if (!reportData?.params) {
      toast({
        className: 'share-report-error-toast',
        title: messages.genericToastTitleError,
        description: 'Report data not available. Please try again later.',
        variant: 'destructive',
      });
      return;
    }

    // Validate that all selected groups still exist in the available groups
    if (data.share_with.length > 0) {
      const invalidGroups = data.share_with.filter(
        (selectedGroup) =>
          !shareGroups?.some(
            (availableGroup) => availableGroup.uuid === selectedGroup.value,
          ),
      );

      if (invalidGroups.length > 0) {
        console.error('Invalid group UUIDs found:', invalidGroups);
        toast({
          className: 'share-report-error-toast',
          title: messages.genericToastTitleError,
          description:
            'Some selected groups are no longer available. Please refresh and try again.',
          variant: 'destructive',
        });
        return;
      }
    }

    setIsSubmitting(true);
    onSharingChange?.(true); // Notify parent that sharing started

    try {
      // Take the existing parsed parameters but ONLY the ones the backend expects
      // Filter out all UI settings like ribbonPlugin.settings, wmsSettings, etc.
      const existingParams = reportData.params;

      // Define the parameters that should be sent to the backend for sharing
      // Based on CommonParams + report-specific parameters (no UI settings)
      const backendParams: any = {};

      // CommonParams fields that are always expected by backend
      const commonParamKeys = [
        'RDN',
        'RDU',
        'NAME',
        'SDAUSR',
        'EDAUSR',
        'SDAISO',
        'EDAISO',
        'SDATS',
        'EDATS',
        'RS',
        'MC',
        'SUID',
        'RSA',
        'MDUR',
      ];

      // Report-specific parameter keys (varies by report type)
      const reportSpecificKeys = [
        'GAC',
        'STG',
        'SGA',
        'PI',
        'GGD',
        'SHPL',
        'FID',
        'FN',
        'FI', // Area params
        'TL',
        'IGPS', // Ships params
        'CB',
        'CBCD', // Track params
        'CPD',
        'CS',
        'CD',
        'SMMLMIN',
        'SMMLMAX', // Density params
        'PAC',
        'DIST',
        'TIME', // Other common params
      ];

      // Copy only backend parameters, skip UI settings
      [...commonParamKeys, ...reportSpecificKeys].forEach((key) => {
        if (existingParams.hasOwnProperty(key)) {
          backendParams[key] = existingParams[key];
        }
      });

      // Only update the sharing settings, keep everything else as-is
      const groupUuids = data.share_with.map((item) => item.value);
      const groupNames = data.share_with.map((item) => item.label);

      // Add sharing-specific parameters
      backendParams.SR = groupUuids; // Group IDs for sharing
      backendParams.ACL = groupNames; // Group names for ACL
      backendParams.RSE = false; // Set to false to indicate sharing update

      const updatedParams = backendParams;

      await new Promise((resolve, reject) => {
        mutate(
          {
            type: reportTemplate.type as any,
            params: updatedParams as any, // Type assertion to bypass strict typing
          },
          {
            onSuccess: () => {
              toast({
                className: 'share-report-success-toast',
                title: messages.genericToastTitleSuccess,
                description: 'Report sharing settings updated successfully',
              });
              setIsSubmitting(false);
              onSharingChange?.(false); // Notify parent that sharing ended
              onOpenChange(false);
              resolve(true);
            },
            onError: (error: Error) => {
              console.error('Share report error:', error);

              // Provide more specific error messages
              let errorMessage = 'Failed to update report sharing settings';
              if (error.message.includes('ACL entry not found')) {
                errorMessage =
                  'One or more selected groups are not available for sharing. Please refresh and try again.';
              } else if (error.message.includes('permission')) {
                errorMessage =
                  'You do not have permission to share with the selected groups.';
              }

              toast({
                className: 'share-report-error-toast',
                title: messages.genericToastTitleError,
                description: errorMessage,
                variant: 'destructive',
              });
              setIsSubmitting(false);
              onSharingChange?.(false); // Notify parent that sharing ended
              reject(error);
            },
          },
        );
      });
    } catch (error) {
      console.error('Share report error:', error);
      toast({
        className: 'share-report-error-toast',
        title: messages.genericToastTitleError,
        description: 'Failed to update report sharing settings',
        variant: 'destructive',
      });
      setIsSubmitting(false);
      onSharingChange?.(false); // Notify parent that sharing ended
    }
  };

  const isLoading = isLoadingGroups;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="!max-w-2xl max-h-[90vh] h-[280px]">
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-6 h-full flex flex-col"
          >
            <div className="space-y-4 flex-1">
              <div>
                <h2 className="text-lg font-semibold">Share Report</h2>
                <p className="text-sm text-muted-foreground">
                  Select groups to share this report with. Leave empty to make
                  it private.
                </p>
              </div>

              <div className="space-y-4 py-2">
                <FormField
                  control={form.control}
                  name="share_with"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Share with groups</FormLabel>
                      <FormControl>
                        {isLoading ? (
                          <div className="flex items-center justify-center h-10">
                            <LoadingSpinner className="size-4" />
                            <span className="ml-2 text-sm">
                              Loading groups...
                            </span>
                          </div>
                        ) : (
                          <GroupMultiSelect
                            value={field.value}
                            onChange={field.onChange}
                            disabled={!isOwner(reportTemplate)}
                            disabledReason={
                              messages.genericNotOwnerDisabledText ||
                              'You must be the owner to share this object'
                            }
                          />
                        )}
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <div className="flex justify-end space-x-2 mt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                {messages.genericCancel}
              </Button>
              <Button type="submit" disabled={isSubmitting || isLoading}>
                Update Sharing
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
